/**
 * 合约订单，委托等业务
 */
import Axios from 'axios';
import { action, computed, observable } from 'mobx';
import { BigNumber } from 'bignumber.js';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import { Balance, DepthMergeItem, DisplayAmountConfig, SymbolConfig, TickerInfo, UserSymbolConfig } from './spotData';
import { API, WEBSOCKET_URL_SPOT } from '../config/api';
import UnderLyingEnum from '@/@enums/UnderLyingEnum';
import { OrderItem } from '@/models/spotOrder';
import SpotSocketClient from '@/utils/spotSocketClient';
import SpotWebsocketInstance from '@/models/injection/spotWebsocketInstance';
import { UpDownThemeEnum } from '@/@enums/Color';
import { getCoinValue, getValue } from '@/utils/digits';
import i18n from '@/i18n';
import { BalanceDetail } from '@/models/spotAsset';
import { AmountUnitEnum } from '@/@enums/Unit';
import { getExchangeUrl } from '@/utils/location';
import without from 'lodash/without';

export interface CreateOrderParams {
  symbol: string;
}

export interface DepthData {
  u: string;
  s?: string;
  t?: number;
  a: [string, string][];
  b: [string, string][];
}

export interface DealItem {
  a: number; // 成交量
  i?: string; // 成交ID
  m: number | string; // 买卖方向 // 1 == buy == BID, 2 == sell == ASK
  p: number; // 成交价
  s: string; // 交易对
  t: number; //	成交时间
}

const defaultBuyOrderRowIndex = -1;
const defaultSellOrderRowIndex = 99;

export default class Spot {
  private readonly rootStore: any;

  @observable depthData: DepthData;

  @observable dealData: DealItem[];

  @observable displayAmountConfig: DisplayAmountConfig; // 盘口, 最新成交展示的数量，会根据orderBook的高度决定；

  @observable limitPrice: string; // 设置限价单的价格

  @observable depthMerge: number;

  @observable isShowCurrentSymbolOnly: boolean;

  symbolSocket: SpotSocketClient; // for 'sub_symbol'

  marketSocket: SpotSocketClient; // for 'sub_ticker', 'sub_mark_price', 'sub_kline'

  @observable userSocket: SpotSocketClient | null;

  @observable buyOrderRowIndex: number;

  @observable sellOrderRowIndex: number;
  @observable collectionListSpot: string[];

  constructor(rootStore: any) {
    this.collectionListSpot = [];

    this.rootStore = rootStore;
    this.depthData = {
      a: [],
      b: [],
      u: '-1',
    };
    this.dealData = [];
    this.symbolSocket = SpotWebsocketInstance(WEBSOCKET_URL_SPOT, 'SYMBOL');
    this.marketSocket = SpotWebsocketInstance(WEBSOCKET_URL_SPOT, 'MARKET');
    this.userSocket = null;
    this.displayAmountConfig = {
      ask: 7,
      bid: 7,
      trade: 7,
    };
    this.limitPrice = '';
    this.depthMerge = 0;
    this.isShowCurrentSymbolOnly = localStorage.getItem('isShowCurrentSymbolOnly') === 'true';
    this.buyOrderRowIndex = defaultBuyOrderRowIndex;
    this.sellOrderRowIndex = defaultSellOrderRowIndex;
  }

  readonly balance = observable<string, Balance>(new Map());

  @action.bound
  setBuyOrderRowIndex(value: number) {
    this.buyOrderRowIndex = value;
  }
  // 收藏列表

  @action.bound
  setCollectionListSpot(data: string[]) {
    this.collectionListSpot = data;
  }
  @action.bound
  setSellOrderRowIndex(value: number) {
    this.sellOrderRowIndex = value;
  }

  @action.bound
  resetOrderRowIndex() {
    this.buyOrderRowIndex = defaultBuyOrderRowIndex;
    this.sellOrderRowIndex = defaultSellOrderRowIndex;
  }

  @action.bound
  createUserSocket() {
    this.userSocket = SpotWebsocketInstance(WEBSOCKET_URL_SPOT, 'USER');
  }

  @computed get currentFee(): { makerFee: number; takerFee: number } {
    const { makerFee: userMakerFee, takerFee: userTakerFee } = this.rootStore.stepRate.userStepRate;
    const { makerFee: configMakerFee, takerFee: configTakerFee } = this.rootStore.spotTicker.currentSymbolConfig;

    return { makerFee: userMakerFee || configMakerFee, takerFee: userTakerFee || configTakerFee };
  }

  @action.bound
  setDisplayAmountConfig(data: DisplayAmountConfig) {
    this.displayAmountConfig = { ...this.displayAmountConfig, ...data };
  }

  @computed get displayHeight() {
    const { ask, bid, trade } = this.displayAmountConfig;

    return {
      ask: `${ask * 20}px`,
      bid: `${bid * 20}px`,
      trade: `${trade * 20}px`,
    };
  }

  // @loading('future/createOrder')
  // @action
  // async createOrder({ symbol }: CreateOrderParams) {
  //   return Axios.post(API.SPOT_CREATE_ORDER, {
  //     symbol,
  //   });
  // }
  //
  // @loading('future/getOrder')
  // @action
  // async getOrder({ symbol }: CreateOrderParams) {
  //   const res = await Axios.get(API.SPOT_CREATE_ORDER, {
  //     symbol,
  //   });
  //
  //   runInAction(() => {
  //     this.balance.set('aa', res.data);
  //   });
  // }

  //
  // 深度
  //

  @action.bound
  setDepthData(data: DepthData) {
    const { s } = data;

    // 初始化用
    if (s === '') {
      this.depthData = data;
    } else if (s === this.rootStore.spotTicker.currentSymbolIdentity) {
      this.depthData = data;
    }
  }

  @action.bound
  canInsertDepth(u: string) {
    return new BigNumber(u).gt(this.depthData.u);
  }

  /**
   * 确保depthData的数据中，数量都是大于0的
   * @param params
   */
  @action.bound
  async fetchDepthData(params: { symbol: string; level: number }) {
    const { symbol } = params;

    if (!symbol) return;

    const defaultValue = { s: symbol, t: 0, a: [], b: [], u: '-1' };

    const result: any = await Axios.get(API.SPOT_TICKER_DEPTH_LIST, {
      params: {
        symbol,
        level: 30,
      },
    }).catch(() => {
      this.setDepthData(defaultValue);
    });

    if (result) {
      const { s, t, a, b, u } = result;

      if (Array.isArray(a) && Array.isArray(b)) {
        this.setDepthData({
          u,
          s,
          t,
          a: a.filter(item => new BigNumber(item[1]).gt(0)),
          b: b.filter(item => new BigNumber(item[1]).gt(0)),
        });
      } else {
        this.setDepthData(defaultValue);
      }
    } else {
      this.setDepthData(defaultValue);
    }
  }

  /**
   * 更新深度图中的某一档
   * @param data
   * 注意事项：
   * 1. 判断档位的数量是否为0
   * 2. 最大深度
   */
  @action.bound
  setDepthDataByWS(data: DepthData) {
    const currentSymbolIdentity = this.rootStore.spotTicker.currentSymbolIdentity;
    const { s, a, b, u } = this.depthData;
    const { s: newS, a: newA, b: newB } = data;

    if (currentSymbolIdentity !== newS) {
      return;
    }

    const tempAObj: any = {};
    const tempBObj: any = {};

    if (Array.isArray(a) && Array.isArray(newA)) {
      [...a, ...newA].forEach((item: [string, string]) => {
        tempAObj[item[0]] = item[1];
      });
    }

    if (Array.isArray(b) && Array.isArray(newB)) {
      [...b, ...newB].forEach((item: [string, string]) => {
        tempBObj[item[0]] = item[1];
      });
    }

    this.setDepthData({
      u,
      s,
      a: Object.entries(tempAObj)
        .filter((item: any) => new BigNumber(item[1]).gt(0))
        .sort((i: any, j: any) => {
          // 卖盘从小到大
          const later = new BigNumber(i[0]);
          return later.minus(j[0]).toNumber();
        }) as [string, string][],
      b: Object.entries(tempBObj)
        .filter((item: any) => new BigNumber(item[1]).gt(0))
        .sort((i: any, j: any) => {
          // 买盘从大到小
          const later = new BigNumber(j[0]);
          return later.minus(i[0]).toNumber();
        }) as [string, string][],
    });

    //   const { ba, p, q, s: symbol, id } = data;
    //
    //   if (currentSymbolIdentity !== symbol) {
    //     return;
    //   }
    //
    //   if (!Array.isArray(a) || !Array.isArray(b)) return;
    //
    //   u = id;
    //   let replace = ba === 1 ? tempB : tempA;
    //   const index = replace.findIndex((item: any) => new BigNumber(p).isEqualTo(item[0]));
    //
    //   // 价格档位存在，直接替换
    //   if (index > -1) {
    //     // 数量为0时从深度图去掉
    //     replace.splice(index, 1, [p, q]);
    //   } else {
    //     // 新的价格档位，插入后排序
    //     // TODO：如果达到了深度的上限，是否要截取
    //
    //     replace.push([p, q]);
    //
    //     replace = replace.sort((i: any, j: any) => {
    //       if (ba === 1) {
    //         // 买盘从大到小
    //         const later = new BigNumber(j[0]);
    //         return later.minus(i[0]).toNumber();
    //       }
    //       // 卖盘从小到大
    //       const later = new BigNumber(i[0]);
    //       return later.minus(j[0]).toNumber();
    //     });
    //   }
    //
    //   tempA = ba === 2 ? replace : a;
    //   tempB = ba === 1 ? replace : b;
    //
    // this.setDepthData({
    //   u,
    //   s,
    //   a: tempA.filter(item => new BigNumber(item[1]).gt(0)),
    //   b: tempB.filter(item => new BigNumber(item[1]).gt(0)),
    // });
  }

  // 所有深度的个数累加，供深度图使用
  @computed get depthSumData() {
    const { a, b } = this.depthData;

    let sumA = new BigNumber(0);
    for (let i = 0; i < a?.length; i += 1) {
      sumA = sumA.plus(a[i][1]);
    }

    let sumB = new BigNumber(0);
    for (let i = 0; i < b?.length; i += 1) {
      sumB = sumB.plus(b[i][1]);
    }

    return {
      a: sumA.valueOf(),
      b: sumB.valueOf(),
      max: BigNumber.max(sumA, sumB).valueOf(),
    };
  }

  @computed get depthDataForFull() {
    const depthMerge = this.depthMerge;
    const depthPrecisionMergeList = this.rootStore.spotTicker.currentSymbolDepthPrecisionMergeList;
    const orderList = this.rootStore.spotOrder.orderList;
    const { a, b } = this.depthData;
    const depthSumData = this.depthSumData;

    if (depthMerge) {
      const buyData = this.generateDepthDataForDisplay(
        'BUY',
        b,
        depthMerge,
        depthSumData.max,
        depthPrecisionMergeList,
        orderList,
      );
      const sellData = this.generateDepthDataForDisplay(
        'SELL',
        a,
        depthMerge,
        depthSumData.max,
        depthPrecisionMergeList,
        orderList,
      ); // 从大到小排列

      return {
        a: sellData,
        b: buyData,
      };
    }

    return {
      a: [],
      b: [],
    };
  }

  // 根据板块高度算出需要展示的深度数据
  @computed get depthDisplayData() {
    const { ask, bid } = this.displayAmountConfig;
    const { a, b } = this.depthData;
    let aResult: any = [];
    let bResult: any = [];

    if (Array.isArray(a)) {
      const aLength = a.length;
      aResult = a.slice(0, Math.min(ask, aLength));
    }

    if (Array.isArray(b)) {
      const bLength = b.length;
      bResult = b.slice(0, Math.min(bid, bLength));
    }

    return {
      a: aResult,
      b: bResult,
    };
  }

  // 展示的深度张数累加，用于计算盘口的背景百分比
  @computed get depthDisplaySumData() {
    const { a, b } = this.depthDisplayData;

    let sumA = new BigNumber(0);
    for (let i = 0; i < a?.length; i += 1) {
      sumA = sumA.plus(a[i][1]);
    }

    let sumB = new BigNumber(0);
    for (let i = 0; i < b?.length; i += 1) {
      sumB = sumB.plus(b[i][1]);
    }

    return {
      a: sumA.valueOf(),
      b: sumB.valueOf(),
      max: BigNumber.max(sumA, sumB).valueOf(),
    };
  }

  @action.bound
  generateDepthDataForDisplay(
    side: OrderSide,
    data: [string, string][],
    depthMerge: number,
    sumData: string,
    depthPrecisionMergeList: any[],
    orderList: OrderItem[],
  ) {
    if (depthPrecisionMergeList.length > 0) {
      const depthMergeItem = depthPrecisionMergeList.find((item: DepthMergeItem) => depthMerge === item.value);
      const base = depthMergeItem?.base || 1;
      const pricePrecision = depthMergeItem?.precision || 0;
      const sellCoinPrecision = this.rootStore.spotTicker.currentSymbolPrecision.sellCoinPrecision;

      const createPrice = (
        orderSide: OrderSide,
        price: string | number,
        priceBase: string | number,
        precision: number,
      ) => {
        if (orderSide === 'BUY') {
          return new BigNumber(price)
            .idiv(priceBase)
            .times(priceBase)
            .toFixed(pricePrecision);
        }

        return new BigNumber(new BigNumber(price).div(priceBase).toFixed(0, 0)).times(priceBase).toFixed(precision);
      };

      const orderPriceList = orderList
        .filter((item: OrderItem) => item.orderSide === side)
        .map((item: OrderItem) => ({
          ...item,
          price: createPrice(item.orderSide, item.price, base, pricePrecision),
        }));

      // 1. 过滤非0数据；
      // 2. 根据深度格式化价格；
      // 3. 生成所需数据；
      let amountSum = new BigNumber(0);
      const data1 = data?.filter((item: [string, string]) => new BigNumber(item[1]).gt(0)) || [];
      const data2: [string, string][] = [];

      data1.forEach(([price, amount]: [string, string]) => {
        const currentPrice = createPrice(side, price, base, pricePrecision);

        const lastItem = last(data2);

        if (lastItem) {
          const [lastPrice, lastAmount] = lastItem;

          if (new BigNumber(currentPrice).eq(lastPrice)) {
            data2[data2.length - 1][1] = new BigNumber(amount).plus(lastAmount).toString();
            return;
          }
        }

        if (new BigNumber(currentPrice).gt(0)) {
          data2.push([currentPrice, amount]);
        }
      });

      return data2.map((item: [string, string]) => {
        const currentPrice = item[0];
        const currentAmount = new BigNumber(item[1]);
        amountSum = amountSum.plus(currentAmount);

        const computedRate = amountSum
          .div(new BigNumber(sumData))
          .times(100)
          .toNumber();
        return [
          currentPrice, // 价格
          currentAmount.valueOf(), // 数量
          amountSum.valueOf(), // 合计
          computedRate > 100 ? 100 : computedRate, // 背景比例
          orderPriceList.findIndex((order: OrderItem) => new BigNumber(currentPrice).eq(order.price)) > -1, // 是否是订单所处价格
        ];
      });
    }

    return [];
  }

  /**
   * return: [price, amount, total, rate, isOrderPosition]
   */
  @computed get depthDisplayDataForFull() {
    const depthMerge = this.depthMerge;
    const depthPrecisionMergeList = this.rootStore.spotTicker.currentSymbolDepthPrecisionMergeList;
    const orderList = this.rootStore.spotOrder.orderList;
    const { a, b } = this.depthDisplayData;
    const depthDisplaySumData = this.depthDisplaySumData;

    if (depthMerge) {
      const buyData = this.generateDepthDataForDisplay(
        'BUY',
        b,
        depthMerge,
        depthDisplaySumData.max,
        depthPrecisionMergeList,
        orderList,
      );
      const sellData = this.generateDepthDataForDisplay(
        'SELL',
        a,
        depthMerge,
        depthDisplaySumData.max,
        depthPrecisionMergeList,
        orderList,
      ); // 从大到小排列

      return {
        a: sellData,
        b: buyData,
      };
    }

    // 当 depthMerge=0 时，需要转换数据格式以包含累积总量
    // Tooltip 需要的格式：[price, amount, total, rate, isOrderPosition]
    const convertToFullFormat = (data: [string, string][]): any[] => {
      let amountSum = new BigNumber(0);
      return data.map(([price, amount]) => {
        amountSum = amountSum.plus(amount);
        const computedRate = depthDisplaySumData.max ?
          amountSum.div(new BigNumber(depthDisplaySumData.max)).times(100).toNumber() : 0;
        return [
          price,
          amount,
          amountSum.valueOf(), // 累积总量
          computedRate > 100 ? 100 : computedRate,
          false, // 没有深度合并时不标记订单位置
        ];
      });
    };

    return {
      a: a && a.length > 0 ? convertToFullFormat(a) : [],
      b: b && b.length > 0 ? convertToFullFormat(b) : [],
    };
  }

  //
  // 最新成交
  //

  @action.bound
  setDealData(data: DealItem[]) {
    const firstItem = data[0];

    if (firstItem) {
      const { s } = firstItem;

      if (s === this.rootStore.spotTicker.currentSymbolIdentity) {
        this.dealData = data;
      }
    } else {
      this.dealData = data;
    }
  }

  @action.bound
  async fetchDealData(params: { symbol: string; num?: number }) {
    const { symbol, num = 30 } = params;

    if (!symbol) return;

    const result: any = await Axios.get(API.SPOT_TICKER_TRADE_LATEST_LIST, {
      params: {
        symbol,
        num,
      },
    });

    if (result) {
      this.setDealData(result);
    }
  }

  /**
   * 添加新的成交记录
   * 一般是websocket推送
   * @param data
   * 注意事项
   * 1. 最大记录
   */
  @action.bound
  setDealDataByWS(data: DealItem[]) {
    const currentSymbolIdentity = this.rootStore.spotTicker.currentSymbolIdentity;

    if (Array.isArray(data) && data.length > 0) {
      const { s } = data[0];

      if (s !== currentSymbolIdentity) {
        return;
      }

      // TODO: 需要知道最大显示的数量
      this.setDealData([...data, ...this.dealData].slice(0, 30));
    }
  }

  @computed get valuationUnit() {
    const { underlying, spotTicker } = this.rootStore;
    const { currentSymbolConfig } = spotTicker;
    const { currentUnderlying } = underlying;
    return currentUnderlying === UnderLyingEnum.U_BASED ? 'USDT' : currentSymbolConfig?.baseCoin?.toUpperCase();
  }

  @computed get priceUnit() {
    return this.rootStore.underlying.currentUnderlying === UnderLyingEnum.U_BASED ? 'USDT' : 'USD';
  }

  //
  // 持仓板块，列表
  //

  @computed get orderListWithConfig() {
    const getPrecision = this.rootStore.spotTicker.getPrecision;
    const orderList = cloneDeep(this.rootStore.spotOrder.orderList);

    return orderList.map((item: OrderItem) => {
      const { symbol, price, origQty, avgPrice, executedQty } = item;
      const { buyCoinPrecision } = getPrecision(symbol);

      item.orderVolume = new BigNumber(avgPrice).times(executedQty).toFixed(buyCoinPrecision, 0);
      return item;
    });
  }

  // 有持仓数的持仓
  @computed get orderListWithValue() {
    return this.orderListWithConfig.filter((item: OrderItem) => new BigNumber(item.origQty).gt(0));
  }

  // 当前交易对限价委托,持仓数可能为0
  @computed get currentSymbolActiveOrder(): { long: OrderItem[]; short: OrderItem[] } {
    const currentSymbol = this.rootStore.spotTicker.currentSymbolIdentity;
    const orderListWithConfig = this.orderListWithConfig;

    return {
      long: orderListWithConfig.filter(
        (item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'LONG',
      ),
      short: orderListWithConfig.filter(
        (item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'SHORT',
      ),
    };
  }

  // TODO: 订单名义价值的替代计算方式，稳定后删除
  // 当前交易对未成交订单价值
  // 注意要减去已成交的数量
  // @computed get currentSymbolOrderValueOfActive(): { long: string; short: string } {
  //   const { long: longOrder, short: shortOrder } = this.currentSymbolActiveOrder;
  //   let longResult = new BigNumber(0);
  //   let shortResult = new BigNumber(0);
  //
  //   longOrder.forEach((item: OrderItem) => {
  //     longResult = longResult.plus(
  //       getValue(
  //         item.price,
  //         new BigNumber(item?.origQty || 0).minus(item?.executedQty || 0).valueOf(),
  //         item.contractSize,
  //       ),
  //     );
  //   });
  //
  //   shortOrder.forEach((item: OrderItem) => {
  //     shortResult = shortResult.plus(
  //       getValue(
  //         item.price,
  //         new BigNumber(item?.origQty || 0).minus(item?.executedQty || 0).valueOf(),
  //         item.contractSize,
  //       ),
  //     );
  //   });
  //
  //   return {
  //     long: longResult.valueOf(),
  //     short: shortResult.valueOf(),
  //   };
  // }

  @computed get orderRecordWithConfig() {
    const getPrecision = this.rootStore.spotTicker.getPrecision;
    const targetList = cloneDeep(this.rootStore.spotOrder.orderRecord);

    return targetList.map((item: OrderItem) => {
      const { symbol } = item;
      const { price, origQty } = item;
      const { buyCoinPrecision } = getPrecision(symbol);
      item.orderVolume = new BigNumber(price).times(origQty).toFixed(buyCoinPrecision, 0);
      return item;
    });
  }

  // @action.bound
  // generateBalanceData(origin: BalanceDetail, positionList: PositionItem[]) {
  //   const result: CoinBalance = { ...origin } as CoinBalance;
  //
  //   const { coin, walletBalance, availableBalance, isolatedMargin, crossedMargin } = origin;
  //   let floatProfit = new BigNumber(0);
  //   // 该结算货币下的全仓未实现盈亏
  //   let crossedFloatProfit = new BigNumber(0);
  //   // 全仓仓位未实现亏损
  //   let crossedFloatLoss = new BigNumber(0);
  //
  //   if (coin === 'usdt') {
  //     positionList.forEach((p: PositionItem) => {
  //       if (p.symbol?.split('_')[1] === 'usdt') {
  //         const fp = p.floatProfit || 0;
  //         floatProfit = floatProfit.plus(fp);
  //
  //         if (p.positionType === 'CROSSED') {
  //           crossedFloatProfit = crossedFloatProfit.plus(fp);
  //
  //           if (new BigNumber(fp).lt(0)) {
  //             crossedFloatLoss = crossedFloatLoss.plus(fp);
  //           }
  //         }
  //       }
  //     });
  //   } else {
  //     positionList.forEach((p: PositionItem) => {
  //       if (p.symbol?.split('_')[0] === coin) {
  //         const fp = p.floatProfit || 0;
  //         floatProfit = floatProfit.plus(fp);
  //
  //         if (p.positionType === 'CROSSED') {
  //           crossedFloatProfit = crossedFloatProfit.plus(fp);
  //
  //           if (new BigNumber(fp).lt(0)) {
  //             crossedFloatLoss = crossedFloatLoss.plus(fp);
  //           }
  //         }
  //       }
  //     });
  //   }
  //
  //   result.floatProfit = floatProfit.valueOf();
  //   // 总权益 = 钱包余额 + 未实现盈亏
  //   result.equity = new BigNumber(walletBalance).plus(floatProfit).valueOf();
  //   // 可用余额 = max(0，真实可用 + ∑该结算货币下的全仓未实现盈亏)
  //   result.availableBalanceDisplay = BigNumber.max(
  //     0,
  //     new BigNumber(availableBalance).plus(crossedFloatProfit),
  //   ).valueOf();
  //   // 净资产余额 = max(0，真实可用 - abs(∑全仓仓位未实现亏损))
  //   result.netBalance = BigNumber.max(0, new BigNumber(availableBalance).minus(crossedFloatLoss.abs())).valueOf();
  //   // 仓位保证金 = ∑逐仓仓位保证金 + ∑全仓仓位起始保证金
  //   result.positionMargin = new BigNumber(isolatedMargin).plus(crossedMargin).valueOf();
  //
  //   return result;
  // }

  @computed get balanceData(): BalanceDetail {
    const config = this.rootStore.spotTicker.currentSymbolConfig;
    const { buyCoin } = config;
    const spotBalanceList = this.rootStore.spotAsset.spotBalanceList;
    return (
      spotBalanceList.find((balance: BalanceDetail) => balance.coin === buyCoin) || {
        availableBalance: '0',
        coin: '',
        balance: '0',
        estimatedAvailableAmount: '0',
        estimatedCoinType: '',
        estimatedCynAmount: '0',
        estimatedTotalAmount: '0',
        freeze: '0',
      }
    );
  }

  @computed get displayCoinBalanceData() {
    const config = this.rootStore.spotTicker.currentSymbolConfig;
    const { buyCoin, sellCoin } = config;
    const spotBalanceList = this.rootStore.spotAsset.spotBalanceList;
    return {
      sellCoinBalance: spotBalanceList.find((balance: BalanceDetail) => balance.coin === sellCoin),
      buyCoinBalance: spotBalanceList.find((balance: BalanceDetail) => balance.coin === buyCoin),
    };
  }

  // 当前交易对持仓
  // @computed get currentSymbolPosition(): { long: PositionItem; short: PositionItem } {
  //   const currentSymbol = this.rootStore.spotTicker.currentSymbolIdentity;
  //   const orderListWithConfig = this.orderListWithConfig;
  //
  //   return {
  //     long: orderListWithConfig.find(
  //       (item: PositionItem) => currentSymbol === item.symbol && item.positionSide === 'LONG',
  //     ),
  //     short: orderListWithConfig.find(
  //       (item: PositionItem) => currentSymbol === item.symbol && item.positionSide === 'SHORT',
  //     ),
  //   } as any;
  // }

  // 当前交易对持仓价值
  // @computed get currentSymbolPositionValue(): { long: string; short: string } {
  //   const { long: longPosition, short: shortPosition } = this.currentSymbolPosition;
  //   const currentUnderlying = this.rootStore.underlying.currentUnderlying;
  //
  //   const long = longPosition
  //     ? currentUnderlying === UnderLyingEnum.U_BASED
  //       ? getValue(longPosition.entryPrice, longPosition.positionSize, longPosition.contractSize)
  //       : getCoinValue(longPosition.entryPrice, longPosition.positionSize, longPosition.contractSize)
  //     : '0';
  //   const short = shortPosition
  //     ? currentUnderlying === UnderLyingEnum.U_BASED
  //       ? getValue(shortPosition.entryPrice, shortPosition.positionSize, shortPosition.contractSize)
  //       : getCoinValue(shortPosition.entryPrice, shortPosition.positionSize, shortPosition.contractSize)
  //     : '0';
  //
  //   return {
  //     long,
  //     short,
  //   };
  // }

  // 当前交易对订单
  // @computed get currentSymbolOrder(): { long: OrderItem[]; short: OrderItem[] } {
  //   const currentSymbol = this.rootStore.spotTicker.currentSymbolIdentity;
  //   const orderList = this.rootStore.spotOrder.orderList;
  //
  //   return {
  //     long: orderList.filter((item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'LONG'),
  //     short: orderList.filter((item: OrderItem) => currentSymbol === item.symbol && item.positionSide === 'SHORT'),
  //   };
  // }

  // 当前交易对订单名义价值
  // U本位
  // 1. 多仓订单名义价值 = 订单占用保证金 / (初始保证金 + 2 * taker费率)
  // 2. 空仓订单名义价值 = 订单占用保证金 / (起始保证金率 * (1 + Taker费率) + Taker费率 * (1 - 维持保证金率))
  // 币本位
  // 多仓：订单名义价值 = 订单占用保证金 / (起始保证金率 * (1 + Taker费率) + Taker费率 * (1 - 维持保证金率【杠杆倍数】))
  // 空仓：订单名义价值 = 订单占用保证金 / (起始保证金率 + 2 * Taker费率)
  // @computed get currentSymbolOrderValue(): { long: string; short: string } {
  //   const { long: longOrder, short: shortOrder } = this.currentSymbolOrder;
  //   const { long: longConfig, short: shortConfig } = this.userCurrentSymbolConfig;
  //   const { takerFee } = this.currentFee;
  //   const getLeverageMaxBracket = this.rootStore.spotTicker.getLeverageMaxBracket;
  //   const isUBased = this.rootStore.underlying.isUBased;
  //   const longLeverage = longConfig.leverage;
  //   const shortLeverage = shortConfig.leverage;
  //
  //   let longResult = new BigNumber(0);
  //   let shortResult = new BigNumber(0);
  //   if (isUBased) {
  //     longOrder.forEach((item: OrderItem) => {
  //       const value = new BigNumber(item.marginFrozen).div(
  //         new BigNumber(1).div(longLeverage).plus(new BigNumber(takerFee).times(2)),
  //       );
  //       longResult = longResult.plus(value);
  //     });
  //
  //     shortOrder.forEach((item: OrderItem) => {
  //       const value = new BigNumber(item.marginFrozen).div(
  //         new BigNumber(1)
  //           .div(shortLeverage)
  //           .times(new BigNumber(1).plus(takerFee))
  //           .plus(new BigNumber(1).minus(getLeverageMaxBracket(shortLeverage)?.maintMarginRate || 0).times(takerFee)),
  //       );
  //       shortResult = shortResult.plus(value);
  //     });
  //   } else {
  //     longOrder.forEach((item: OrderItem) => {
  //       const value = new BigNumber(item.marginFrozen).div(
  //         new BigNumber(1)
  //           .div(longLeverage)
  //           .times(new BigNumber(1).plus(takerFee))
  //           .plus(new BigNumber(1).minus(getLeverageMaxBracket(longLeverage)?.maintMarginRate || 0).times(takerFee)),
  //       );
  //       longResult = longResult.plus(value);
  //     });
  //
  //     shortOrder.forEach((item: OrderItem) => {
  //       const value = new BigNumber(item.marginFrozen).div(
  //         new BigNumber(1).div(shortLeverage).plus(new BigNumber(takerFee).times(2)),
  //       );
  //       shortResult = shortResult.plus(value);
  //     });
  //   }
  //
  //   return {
  //     long: longResult.isNaN() ? '0' : longResult.valueOf(),
  //     short: shortResult.isNaN() ? '0' : shortResult.valueOf(),
  //   };
  // }

  // 交易对是否有持仓和委托
  // @computed get isCleanSymbol() {
  //   const currentSymbolPosition = this.currentSymbolPosition;
  //   const currentSymbolOrder = this.currentSymbolOrder;
  //   const currentSymbolPlan = this.currentSymbolPlan;
  //
  //   const result = {
  //     long: true,
  //     short: true,
  //   };
  //
  //   if (
  //     (currentSymbolPosition.long && new BigNumber(currentSymbolPosition.long.positionSize).gt(0)) ||
  //     (currentSymbolOrder.long && currentSymbolOrder.long.length > 0) ||
  //     (currentSymbolPlan.long && currentSymbolPlan.long.length > 0)
  //   ) {
  //     result.long = false;
  //   }
  //
  //   if (
  //     (currentSymbolPosition.short && new BigNumber(currentSymbolPosition.short.positionSize).gt(0)) ||
  //     (currentSymbolOrder.short && currentSymbolOrder.short.length > 0) ||
  //     (currentSymbolPlan.short && currentSymbolPlan.short.length > 0)
  //   ) {
  //     result.short = false;
  //   }
  //
  //   return result;
  // }

  @action.bound
  setLimitPrice(value: string) {
    this.limitPrice = value;
  }

  @computed get priceSide() {
    const upDownTheme = this.rootStore.ui.upDownTheme;
    const length = this.dealData.length;
    const result: any = {
      side: '',
      className: '',
      latestPrice: '',
      prevPrice: '',
    };

    if (length > 1) {
      const latestTrade1 = this.dealData[0];
      const latestTrade2 = this.dealData[1];
      const { p: p1 } = latestTrade1;
      const { p: p2 } = latestTrade2;

      result.latestPrice = p1;
      result.prevPrice = p2;

      if (new BigNumber(p1).gt(p2)) {
        result.side = 'UP';
        result.className = upDownTheme === UpDownThemeEnum.greenUpRedDown ? 'text-green' : 'text-red';
      } else if (new BigNumber(p1).lt(p2)) {
        result.side = 'DOWN';
        result.className = upDownTheme === UpDownThemeEnum.greenUpRedDown ? 'text-red' : 'text-green';
      }
    }

    return result;
  }

  // 根据用户持仓信息得到所处的风险限额档位；
  // 存储用户交易对所处档位的多空持仓的杠杆倍数，维持保证金，最大杠杆，最小杠杆等
  // @computed get userCurrentSymbolConfig(): UserSymbolConfig {
  //   const symbol = this.rootStore.spotTicker.currentSymbolIdentity;
  //   const leverageBracketList = this.rootStore.spotTicker.currentSymbolLeverageBrackets;
  //   const symbolConfig = this.rootStore.spotTicker.currentSymbolConfig;
  //   const positionList = this.rootStore.spotPosition.positionList;
  //   const latestMarkPrice = this.rootStore.spotTicker.latestMarkPrice;
  //
  //   const { contractSize } = symbolConfig;
  //
  //   const longPosition = positionList.find(
  //     (item: PositionItem) => item.symbol === symbol && item.positionSide === 'LONG',
  //   );
  //   const shortPosition = positionList.find(
  //     (item: PositionItem) => item.symbol === symbol && item.positionSide === 'SHORT',
  //   );
  //
  //   const longSize = longPosition?.positionSize || 0;
  //   const shortSize = shortPosition?.positionSize || 0;
  //   const longBracket = leverageBracketList.find((item: LeverageBracketItem) =>
  //     new BigNumber(new BigNumber(latestMarkPrice).times(longSize).times(contractSize)).lte(item.maxNominalValue),
  //   );
  //   const shortBracket = leverageBracketList.find((item: LeverageBracketItem) =>
  //     new BigNumber(new BigNumber(latestMarkPrice).times(shortSize).times(contractSize)).lte(item.maxNominalValue),
  //   );
  //
  //   const createBrakcet = (position: PositionItem, bracket: LeverageBracketItem) => {
  //     const { maxNominalValue, initLeverage = 20, basicMaintMarginRate, initPositionType } = symbolConfig;
  //     const maxAmount = bracket?.maxNominalValue
  //       ? new BigNumber(bracket.maxNominalValue)
  //           .dividedBy(contractSize)
  //           .idiv(latestMarkPrice)
  //           .valueOf()
  //       : new BigNumber(maxNominalValue)
  //           .dividedBy(contractSize)
  //           .idiv(latestMarkPrice)
  //           .valueOf();
  //
  //     const positionSize = position?.positionSize || 0;
  //
  //     return {
  //       leverage: position?.leverage || initLeverage,
  //       positionType: position?.positionType || initPositionType,
  //       maxLeverage: bracket ? bracket.maxLeverage : 100,
  //       minLeverage: bracket ? bracket.minLeverage : 1,
  //       maintMarginRate: bracket ? bracket.maintMarginRate : basicMaintMarginRate,
  //       maxNominalValue: bracket ? bracket.maxNominalValue : maxNominalValue,
  //       maxAmount,
  //       availableQty: bracket ? new BigNumber(maxAmount).minus(positionSize).toNumber() : maxNominalValue,
  //       positionSize,
  //     } as UserSymbolConfigItem;
  //   };
  //
  //   return {
  //     symbol,
  //     long: createBrakcet(longPosition, longBracket),
  //     short: createBrakcet(shortPosition, shortBracket),
  //   };
  // }

  // 深度合并
  @action.bound
  setDepthMerge(value: number) {
    const depthMergeObj = this.rootStore.spotTicker.currentSymbolDepthPrecisionMergeList.find(
      (item: DepthMergeItem) => value === item.value,
    );

    let v = value;

    // 如果精度做了调整，本地缓存的的深度不符合要求时
    if (!depthMergeObj) {
      v = 1;
    }

    this.depthMerge = v;
    const depthMergeStorage = localStorage.getItem('depthMerge');
    const obj = JSON.parse(depthMergeStorage || '{}');
    obj[this.rootStore.spotTicker.currentSymbolIdentity] = v;
    localStorage.setItem('depthMerge', JSON.stringify(obj));
  }

  @action.bound
  setIsShowCurrentSymbolOnly(value: boolean) {
    this.isShowCurrentSymbolOnly = value;
    localStorage.setItem('isShowCurrentSymbolOnly', `${value}`);
  }

  @computed get latestPrice() {
    const dealData = this.dealData;

    if (Array.isArray(dealData) && dealData.length > 0) {
      // return dealData[0].p;
      return new BigNumber(dealData[0].p).toFixed();
    }

    return this.rootStore.spotTicker.currentSymbolTicker.c;
  }

  @action.bound
  getLatestPrice() {
    const dealData = this.dealData;
    if (Array.isArray(dealData) && dealData.length > 0) {
      return dealData[0].p;
    }
    return this.rootStore.spotTicker.currentSymbolTicker.c;
  }

  // @action.bound
  // getUnitAmount(
  //   amount: string,
  //   symbol: string,
  //   price: string,
  //   underlyingType?: UnderLyingEnum,
  // ): { amount: string; unitView: string } {
  //   const config = this.rootStore.spotTicker.getSymbolConfig(symbol);
  //   const precision = this.rootStore.spotTicker.getPrecision(symbol);
  //
  //   if (!(config && precision) || new BigNumber(price).lte(0)) {
  //     return {
  //       amount: '--',
  //       unitView: '',
  //     };
  //   }
  //
  //   let unit = this.rootStore.ui.unit;
  //   const uBasedUnit = this.rootStore.ui.uBasedUnit;
  //   const coinBasedUnit = this.rootStore.ui.coinBasedUnit;
  //
  //   if (underlyingType) {
  //     unit = underlyingType === UnderLyingEnum.U_BASED ? uBasedUnit : coinBasedUnit;
  //   }
  //
  //   switch (unit) {
  //     case AmountUnitEnum.sheet:
  //       return {
  //         amount,
  //         unitView: i18n.t('unit.sheet'),
  //       };
  //     case AmountUnitEnum.usdt:
  //       return {
  //         amount: new BigNumber(amount)
  //           .times(config.contractSize)
  //           .times(price)
  //           .toFixed(precision.quoteCoinDisplay, 1),
  //         unitView: 'USDT',
  //       };
  //     case AmountUnitEnum.usd:
  //       return {
  //         amount: new BigNumber(amount).times(config.contractSize).toFixed(0, 1),
  //         unitView: 'USD',
  //       };
  //     default: {
  //       let amountBN: any = '';
  //
  //       if (
  //         (underlyingType && underlyingType === UnderLyingEnum.U_BASED) ||
  //         (!underlyingType && this.rootStore.underlying.isUBased)
  //       ) {
  //         amountBN = new BigNumber(amount).times(config.contractSize);
  //       } else {
  //         amountBN = new BigNumber(amount).times(config.contractSize).div(price);
  //       }
  //
  //       return amountBN.isNaN()
  //         ? {
  //             amount: '--',
  //             unitView: '',
  //           }
  //         : {
  //             amount: amountBN.toFixed(precision.quantity, 1),
  //             unitView: symbol.split('_')[0]?.toUpperCase(),
  //           };
  //     }
  //   }
  // }

  // 市场列表
  @computed get marketList() {
    const tickerList = this.rootStore.spotTicker.spotTickerList;
    const symbolConfigList = this.rootStore.spotTicker.symbolConfigList;

    return symbolConfigList.map((item: SymbolConfig) => {
      const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
        c: '0',
        r: '0',
        v: '0',
        s: item.symbol,
        type: 2,
        l: this.getSymbolCoinNameSpot(item.symbol), //logo图片
      };

      return {
        ...obj,
        hot: item.hot,
        l: this.getSymbolCoinNameSpot(item.symbol), //logo图片
        type: 2,
        labels: item.hot ? ['HOT'] : [],
      };
    });
  }
  // 热门榜
  @computed get marketHotListSpot() {
    const tickerList = this.rootStore.spotTicker.spotTickerList;
    const symbolConfigList = this.rootStore.spotTicker.symbolConfigList;
    return symbolConfigList
      .filter((i: SymbolConfig) => i.hot)
      .map((item: SymbolConfig) => {
        const obj = tickerList.find((o: TickerInfo) => o.s === item.symbol) || {
          c: '0',
          r: '0',
          v: '0',
          s: item.symbol,
          logo: this.getSymbolCoinNameSpot(item.symbol), //logo图片
          type: 2, //类型现货2
        };

        return {
          ...obj,
          hot: item.hot,
          logo: this.getSymbolCoinNameSpot(item.symbol), //logo图片
          type: 2,
          labels: item.hot ? ['HOT'] : [],
        };
      });
  }
  // 涨幅
  @computed get marketUpListSpot() {
    const tickerList = this.rootStore.spotTicker.spotTickerList;
    return tickerList.filter((item: { r: number; }) => item.r > 0).map((item: { s: string; }) => {
      return {
        ...item,
        logo: this.getSymbolCoinNameSpot(item.s), //logo图片
      };
    });
  }
  // 跌幅
  @computed get marketDownListSpot() {
    const tickerList = this.rootStore.spotTicker.spotTickerList;
    return tickerList.filter((item: { r: number; }) => item.r < 0).map((item: { s: string; }, index, _self) => {
      // console.log(_self, 'self===============');
      // console.log(tickerList, 'tickerList===============');
      return {
        ...item,
        logo: this.getSymbolCoinNameSpot(item.s), //logo图片
      };
    }).sort((a, b) => Number(a.r) - Number(b.r));
  }
  // 成交量
  @computed get marketVolListSpot() {
    const tickerList = this.rootStore.spotTicker.spotTickerList;
    return tickerList.filter((item: { v: number; }) => item.v > 0).map((item: { s: string; }) => {
      return {
        ...item,
        logo: this.getSymbolCoinNameSpot(item.s), //logo图片
      };
    });
  }
  // @computed get coinBalanceList(): CoinBalance[] {
  //   const balanceList = this.rootStore.spotAsset.balanceList;
  //   const orderListWithValue = this.orderListWithValue;
  //   const generateBalanceData = this.generateBalanceData;
  //   const isUBased = this.rootStore.underlying.isUBased;
  //
  //   if (isUBased) {
  //     return [];
  //   }
  //
  //   return balanceList.map((item: BalanceDetail): CoinBalance => generateBalanceData(item, orderListWithValue));
  // }

  /**
   * dex（共享保证金） = 钱包余额 - ∑逐仓仓位保证金 - ∑全仓维持保证金 - ∑委托保证金 + ∑除本仓位其他全仓仓位未实现盈亏
   * @param symbol
   * @param positionSide
   */
  // @action.bound
  // getDex(symbol: string, positionSide: PositionSide) {
  //   const positionList = this.rootStore.spotPosition.positionList;
  //   const getPositionBracket = this.rootStore.spotTicker.getPositionBracket;
  //   const getSymbolUnderlyingType = this.rootStore.spotTicker.getSymbolUnderlyingType;
  //   const getSymbolConfig = this.rootStore.spotTicker.getSymbolConfig;
  //   const getFloatProfit = this.getFloatProfit;
  //   const getBalanceByCoin = this.rootStore.spotAsset.getBalanceByCoin;
  //   const isUBased = this.rootStore.underlying.isUBased;
  //   const balance = this.rootStore.spotAsset.balance;
  //
  //   const coin = symbol?.split('_')[0];
  //   const balanceCoin = balance.coin;
  //
  //   const { walletBalance = '0', isolatedMargin = '0', openOrderMarginFrozen = '0' } =
  //     isUBased || coin === balanceCoin ? balance : getBalanceByCoin(coin) || {};
  //
  //   let crossMaintMargin = new BigNumber(0);
  //   let crossedFloatProfit = new BigNumber(0);
  //
  //   positionList.forEach((p: PositionItem) => {
  //     const { symbol: s, positionSide: ps, positionType: pt, entryPrice, positionSize } = p;
  //
  //     if (pt === 'CROSSED' && new BigNumber(positionSize).gt(0)) {
  //       const currentBracket = getPositionBracket(p);
  //       const { maintMarginRate } = currentBracket as LeverageBracketItem;
  //       const underlyingType = getSymbolUnderlyingType(s);
  //       const { contractSize } = getSymbolConfig(s);
  //
  //       if (underlyingType === UnderLyingEnum.U_BASED) {
  //         if (!(s === symbol && ps === positionSide)) {
  //           const fp = getFloatProfit(p);
  //           crossedFloatProfit = crossedFloatProfit.plus(fp || 0);
  //         }
  //
  //         crossMaintMargin = crossMaintMargin.plus(
  //           new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate),
  //         );
  //       } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
  //         // 币本位的计算只关心当前币种
  //         if (s === symbol) {
  //           if (ps !== positionSide) {
  //             const fp = getFloatProfit(p);
  //             crossedFloatProfit = crossedFloatProfit.plus(fp || 0);
  //           }
  //
  //           crossMaintMargin = crossMaintMargin.plus(
  //             new BigNumber(getCoinValue(entryPrice, positionSize, contractSize)).times(maintMarginRate),
  //           );
  //         }
  //       }
  //     }
  //   });
  //
  //   return new BigNumber(walletBalance)
  //     .minus(isolatedMargin)
  //     .minus(crossMaintMargin)
  //     .minus(openOrderMarginFrozen)
  //     .plus(crossedFloatProfit)
  //     .valueOf();
  // }

  // 持仓中的强平价计算
  // @action.bound
  // getLiquidationPrice(position: PositionItem) {
  //   const { symbol, positionSize, positionSide, entryPrice, isolatedMargin, positionType } = position;
  //   const getSymbolConfig = this.rootStore.spotTicker.getSymbolConfig;
  //   const getPositionBracket = this.rootStore.spotTicker.getPositionBracket;
  //   const getSymbolUnderlyingType = this.rootStore.spotTicker.getSymbolUnderlyingType;
  //   const currentSymbolConfig = getSymbolConfig(symbol);
  //   const { contractSize } = currentSymbolConfig;
  //
  //   if (contractSize) {
  //     const underlyingType = getSymbolUnderlyingType(symbol);
  //     const currentBracket = getPositionBracket(position);
  //     const { maintMarginRate } = currentBracket as LeverageBracketItem;
  //
  //     let result = new BigNumber('');
  //
  //     if (underlyingType === UnderLyingEnum.U_BASED) {
  //       if (positionType === 'CROSSED') {
  //         if (positionSide === 'LONG') {
  //           // 多仓强平价格 = (开仓均价 * 数量 * 面值 - dex) / (数量 * 面值)
  //           result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
  //             .minus(this.getDex(symbol, positionSide))
  //             .div(new BigNumber(positionSize).times(contractSize));
  //         } else if (positionSide === 'SHORT') {
  //           // 空仓强平价格 = (开仓均价 * 数量 * 面值 + dex) / (数量 * 面值)
  //           result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
  //             .plus(this.getDex(symbol, positionSide))
  //             .div(new BigNumber(positionSize).times(contractSize));
  //         }
  //       } else if (positionType === 'ISOLATED') {
  //         if (positionSide === 'LONG') {
  //           // 多仓[UP]：强平价格 = (开仓均价 * 数量 * 面值 + 维持保证金 - 仓位保证金) / (数量 * 面值)
  //           result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
  //             .plus(new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate))
  //             .minus(isolatedMargin)
  //             .div(new BigNumber(positionSize).times(contractSize));
  //         } else if (positionSide === 'SHORT') {
  //           // 空仓[DOWN]：强平价格 = (开仓均价 * 数量 * 面值 - 维持保证金 + 仓位保证金) / (数量 * 面值)
  //           result = new BigNumber(getValue(entryPrice, positionSize, contractSize))
  //             .minus(new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate))
  //             .plus(isolatedMargin)
  //             .div(new BigNumber(positionSize).times(contractSize));
  //         }
  //       }
  //     } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
  //       if (positionType === 'CROSSED') {
  //         if (positionSide === 'LONG') {
  //           // 多仓强平价格 = 数量 * 面值 * 开仓均价 / (数量 * 面值 + 开仓均价 * dex)
  //           result = new BigNumber(positionSize)
  //             .times(contractSize)
  //             .times(entryPrice)
  //             .div(
  //               new BigNumber(positionSize)
  //                 .times(contractSize)
  //                 .plus(new BigNumber(entryPrice).times(this.getDex(symbol, positionSide))),
  //             );
  //         } else if (positionSide === 'SHORT') {
  //           // 空仓强平价格 = 数量 * 面值 * 开仓均价 / (数量 * 面值 - 开仓均价 * dex)
  //           result = new BigNumber(positionSize)
  //             .times(contractSize)
  //             .times(entryPrice)
  //             .div(
  //               new BigNumber(positionSize)
  //                 .times(contractSize)
  //                 .minus(new BigNumber(entryPrice).times(this.getDex(symbol, positionSide))),
  //             );
  //         }
  //       } else if (positionType === 'ISOLATED') {
  //         if (positionSide === 'LONG') {
  //           // 多仓[UP]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (仓位保证金 - 维持保证金))
  //           result = new BigNumber(entryPrice)
  //             .times(positionSize)
  //             .times(contractSize)
  //             .div(
  //               new BigNumber(positionSize).times(contractSize).plus(
  //                 new BigNumber(isolatedMargin)
  //                   .minus(
  //                     new BigNumber(positionSize)
  //                       .times(contractSize)
  //                       .div(entryPrice)
  //                       .times(maintMarginRate),
  //                   )
  //                   .times(entryPrice),
  //               ),
  //             );
  //         } else if (positionSide === 'SHORT') {
  //           // 空仓[DOWN]：强平价格 = 开仓均价 * 数量 * 面值 / (数量 * 面值 + 开仓均价 * (维持保证金 - 仓位保证金))
  //           result = new BigNumber(entryPrice)
  //             .times(positionSize)
  //             .times(contractSize)
  //             .div(
  //               new BigNumber(positionSize).times(contractSize).plus(
  //                 new BigNumber(positionSize)
  //                   .times(contractSize)
  //                   .div(entryPrice)
  //                   .times(maintMarginRate)
  //                   .minus(isolatedMargin)
  //                   .times(entryPrice),
  //               ),
  //             );
  //         }
  //       }
  //     }
  //
  //     return result.isNaN() ? '--' : result.valueOf();
  //   }
  //
  //   return '--';
  // }

  @action.bound
  getSymbolName(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.spotTicker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';
    // SYM格式改成 BTC/USDT 的形式
    return `${symbol?.split('_')[0]?.toUpperCase()}/${symbol?.split('_')[1]?.toUpperCase()}`;
  }
  @action.bound
  getSymbolCoinNameSpot(symbol?: string) {
    // const lang = this.rootStore.account.lang;
    // const symbolConfigList = this.rootStore.spotTicker.allSymbolConfigList;
    //
    // const config = symbolConfigList.find((item: SymbolConfig) => item.symbol === symbol);
    //
    // if (config) {
    //   return lang === 'zh' ? config.cnName : config.enName;
    // }
    //
    // return '';

    return symbol?.split('_')[0].toUpperCase();
  }
  // @action.bound
  // getCrossMaintainMargin(position: PositionItem) {
  //   const { symbol, positionSize, entryPrice } = position;
  //   const getPositionBracket = this.rootStore.spotTicker.getPositionBracket;
  //   const getSymbolUnderlyingType = this.rootStore.spotTicker.getSymbolUnderlyingType;
  //   const getSymbolConfig = this.rootStore.spotTicker.getSymbolConfig;
  //
  //   let crossMaintMargin = new BigNumber(0);
  //
  //   const currentBracket = getPositionBracket(position);
  //   const { maintMarginRate } = currentBracket as LeverageBracketItem;
  //   const underlyingType = getSymbolUnderlyingType(symbol);
  //   const { contractSize } = getSymbolConfig(symbol);
  //
  //   if (underlyingType === UnderLyingEnum.U_BASED) {
  //     crossMaintMargin = new BigNumber(getValue(entryPrice, positionSize, contractSize)).times(maintMarginRate);
  //   } else if (underlyingType === UnderLyingEnum.COIN_BASED) {
  //     crossMaintMargin = new BigNumber(getCoinValue(entryPrice, positionSize, contractSize)).times(maintMarginRate);
  //   }
  //
  //   return crossMaintMargin.isNaN() ? '0' : crossMaintMargin.valueOf();
  // }

  @action.bound
  async fetchCollectionListSpot() {
    const result: any = await Axios.get(getExchangeUrl().collectionList);

    if (result) {
      this.setCollectionListSpot(result);
    }
  }

  @action.bound
  addCollectionSpot(symbol: string) {
    if (!this.collectionListSpot.includes(symbol)) {
      const result = [...this.collectionListSpot];
      result.push(symbol);
      this.setCollectionListSpot(result);
      localStorage.setItem('collection-symbols-spot', JSON.stringify({ market: result }));
    }
  }

  @action.bound
  removeCollectionSpot(symbol: string) {
    const originList = [...this.collectionListSpot];
    const result = without(originList, symbol);

    this.setCollectionListSpot(result);
    localStorage.setItem('collection-symbols-spot', JSON.stringify({ market: result }));
  }

  @action.bound
  async fetchAddCollectionSpot(symbol: string) {
    const result: any = await Axios.post(getExchangeUrl().collectionAdd, { symbol });

    if (result === true) {
      if (!this.collectionListSpot.includes(symbol)) {
        symbol.includes(',')
          ? this.setCollectionListSpot([...this.collectionListSpot, ...symbol.split(',')])
          : this.setCollectionListSpot([...this.collectionListSpot, symbol]);
      }
    }
  }

  @action.bound
  async fetchCancelCollectionSpot(symbol: string) {
    const result: any = await Axios.post(getExchangeUrl().collectionRemove, { symbol });

    if (result === true) {
      this.setCollectionListSpot(without(this.collectionListSpot, symbol));
    }
  }
}
